import React, { useState, Suspense, useEffect, useCallback } from "react";
import { useObjectStore } from "../zustand/objects";
import {
  IconTrash,
  IconCopy,
  IconLock,
  IconLockOpen,
  IconListDetails,
  IconNote,
  IconUserPin,
  IconRefresh,
} from '@tabler/icons-react';


// MANTINE
import { Stack, Card, Button, Switch, Text, Group, Slider, ActionIcon, Loader, LoadingOverlay, Textarea, Modal, Tooltip } from '@mantine/core';

import { nanoid } from 'nanoid'

import ModelAttributions from "../ModelAttributions.json";
import { getSinglePose, isFigmaFrameMine } from "../api.js";
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalPrimaryButtonStyles,
  editorModalSecondaryButtonStyles,
} from "../styleFile";

import ObjectTitleName from './ObjectTitleName';
import ModelColourButton from './ModelColourButton';
import TextObjectBackgroundColourButton from './TextObjectBackgroundColourButton';
import LinksToButton from './LinksToButton';
import PosesButton from './PosesButton';
import ChangeScaleLayerListButtons from './ChangeScaleLayerListButtons';

const LayerListItem = ({
  id,
  object,
  category,
  position,
  scale,
  rotation,
  destination,
  content,
  textKind,
  colour,
  bGColour,
  pose,
  title,
  texture,
  isLocked, 
  matrix,
  figmaID,
  image,
  imageSize,
  curved,
  curveAmount,
  uploadedURL,
  objectNotes,
  uiTheme
}) => {
  const {
    updateIsLocked,
    removeObject,
    currentObjectArtboard,
    objectsAreLoaded,
    updateObjectSelected,
    updateTextContent,
    switchImageModalOpen, 
    setImagePurpose,
    setEditingID,
    updateImageTexture,
    copyObject,
    updateFigmaURL,
    switchTextModalOpen,
    arrangeUndoRedo
  } = useObjectStore();

  const borderColour = uiTheme?.controlBorder || "#E5E7EB";
  const iconColour = uiTheme?.controlIcon || "#747B82";
  const panelTextColour = uiTheme?.panelText || "#111827";
  const panelPlaceholderColour = uiTheme?.panelPlaceholder || (panelTextColour === "#111827" ? "#6A7282" : "#9CA3AF");
  
  const removeObjectFunction = (val) => {
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectRemove", 
      artboard: val.currentObjectArtboard, 
      objectID: val.id,
      undo:false
      })
      // send update to zustand
    removeObject(val)

  }

  const [textValue, setTextValue] = useState(content)
  const [textOpen, setTextOpen] = useState(false);
  const [attrOpen, setAttrOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [isMine, setIsMine] = useState(false);

  const onEditTextClick = () => {
    setTextOpen(true)
    switchTextModalOpen(true) 
  }

  const onAttrClick = () => {
    setAttrOpen(true)
  }

  const onTextSubmit = () => {
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectUpdate", 
      artboard: currentObjectArtboard, 
      objectID: id,
      undo:false, 
      field: "content"
      })
      // send update to zustand
    updateTextContent({ id, currentObjectArtboard, content: textValue, undo: false })
    setTextOpen(false)
    switchTextModalOpen(false)
  }

  const onTextCancel = () => {
    setTextOpen(false)
  }

  const onNotesCancel = () => {
    setNotesOpen(false)
  }

  const onAttrCancel = () => {
    setAttrOpen(false)
  }

  const handleChange = (e) => {
    setTextValue(e.target.value)
  }

  const onAddImageTextureClick = () => {
    setEditingID(id)
    switchImageModalOpen()
    setImagePurpose("texture")
}

  const onEditImageURLClick = () => {
    setEditingID(id)
    switchImageModalOpen()
    setImagePurpose("objectEdit")
} 

const onDeleteImageTextureClick = () => {
  // send current state to undoActions
  arrangeUndoRedo({
    updateType: "objectUpdate", 
    artboard: currentObjectArtboard, 
    objectID: id,
    undo:false,
    field: "imageTexture"
    })
    // send update to zustand
  updateImageTexture(id, currentObjectArtboard, null, false)
}

const onLockItemClick = () => {
  // send current state to undoActions
  arrangeUndoRedo({
    updateType: "objectUpdate", 
    artboard: currentObjectArtboard, 
    objectID: id,
    undo:false,
    field: "isLocked"
    })
    // send update to zustand
  updateIsLocked({id: id, undo: false})
  !isLocked ? updateObjectSelected('0') : 
  updateObjectSelected(id)
}
 
  const onCopyItemClick = useCallback(() => {
    const newID = nanoid()
    // send update to zustand first (so we have state to grab for undo array)
    copyObject({ currentObjectArtboard: currentObjectArtboard, id: id, newID: newID, undo: false })
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectAdd",
      artboard: currentObjectArtboard,
      objectID: newID,
      undo: false
    })
  }, [arrangeUndoRedo, copyObject, currentObjectArtboard, id])

const updateFrameURL = () => {
  updateFigmaURL({id: id, currentObjectArtboard: currentObjectArtboard, figmaID: figmaID, undo: false})
}


useEffect(() => {
  category === "figma" && isFigmaFrameMine(figmaID).then(res => setIsMine(res))
  function handleKeyDown(event) {
    if (event.repeat) { return }  
    if (event.key === 'd' && (event.ctrlKey || event.metaKey))  {    
      event.preventDefault();
      onCopyItemClick()
    }
  }
  document.addEventListener("keydown", handleKeyDown)

  return () => document.removeEventListener("keydown", handleKeyDown)
}, [category, figmaID, onCopyItemClick])

  if (!objectsAreLoaded) {
    return <div><Loader color="black" />Loading...</div>;
  } else {
    return (
      <Suspense fallback={null}> 
      <>

        {/* CC ATTRIBUTION Modal */}
        {id !== "camera" && category !== "aiModel" && category !== "shape" && category !== "text" && category !== "image" && category !== "UI" && category !== "figma" && category !== "uploaded" && category !== "apple" && category !== "microsoft" && category!== "floatgridsUi"  && category!== "floatgridsLayout" &&  <AttributionModal attrOpen={attrOpen} onAttrCancel={onAttrCancel} id={id} category={category} object={object} pose={pose} ModelAttributions={ModelAttributions} />}
        {/* Edit notes modal */}
        <NotesModal notesOpen={notesOpen} onNotesCancel={onNotesCancel} id={id} objectNotes={objectNotes} />
        {/* TEXT MODAL */}
        <Modal
          opened={textOpen}
          onClose={onTextCancel}
          centered
          padding={0}
          withCloseButton={false}
        >
         <Card style={{ borderRadius: 4, width: 500, padding: 0, height: 180, overflow: 'hidden' }}>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
          <div>
            <Text size="xl" fw={700}>
              Edit text
            </Text>
            </div>
            <div>
            <Textarea
              value={textValue}
              onChange={handleChange}
            />
            </div>
            <div>
            <Group>
            <Button radius={4} styles={editorModalSecondaryButtonStyles} style={{ marginTop: 15 }} onClick={() => onTextCancel()}>Cancel</Button>
            <Button radius={4} styles={editorModalPrimaryButtonStyles} style={{ marginTop: 15 }} onClick={() => onTextSubmit()}>Submit</Button>
            </Group>
            </div>
            </div>
          </Card>
        </Modal>

         {/* OLD CODE FOR THIS IS IN NOTES (OR GITHUB) */}
          <Stack w={'100%'} gap={7} style={{ color: panelTextColour }}>
            {isLocked ? (
              <Button radius={4} styles={editorModalPrimaryButtonStyles} style={{ marginTop: 5 }} onClick={() => onLockItemClick()} leftSection={<IconLock size={18} />}>Unlock</Button>
            ) : null}

              {/******************************************
                    Edit text and update size buttons 
              ******************************************/}
            <Group justify="space-between" w={'100%'}>
              {category === "text" ? (<Tooltip label="Edit text">
                <ActionIcon onClick={() => !isLocked && onEditTextClick()} size={'md'} variant="outline" aria-label="Settings" color={iconColour} style={{ borderColor: borderColour }}>
                  <IconNote style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon></Tooltip>) :
                <ObjectTitleName open={true} id={id} title={title} borderColour={borderColour} />}
              <ChangeScaleLayerListButtons matrix={matrix} id={id} currentObjectArtboard={currentObjectArtboard} borderColour={borderColour} iconColour={iconColour} />
            </Group>

            {/******************************************
                    Pose, Colour, Background colour and 'Links to' selects
              ******************************************/}
              <Stack gap={1}>
            <PosesButton
              pose={pose}
              id={id}
              object={object}
              uiTheme={{ borderColour, iconColour, textColour: panelTextColour, placeholderColour: panelPlaceholderColour }}
            />
            {id !== "camera" && id !== "image" && category !== "figma" && category !== "apple" && category !== "microsoft" && category !== "floatgridsUi" && category !== "floatgridsLayout" && category !== "uploaded" && (
              <ModelColourButton
                colour={colour}
                id={id}
                uiTheme={{ borderColour, iconColour, textColour: panelTextColour, placeholderColour: panelPlaceholderColour }}
              />
            )}
              {category === "text" && (
                <TextObjectBackgroundColourButton
                  bGColour={bGColour}
                  id={id}
                  uiTheme={{ borderColour, iconColour, textColour: panelTextColour, placeholderColour: panelPlaceholderColour }}
                />
              )}
              <LinksToButton
                id={id}
                destination={destination}
                uiTheme={{ borderColour, iconColour, textColour: panelTextColour, placeholderColour: panelPlaceholderColour }}
              />
              </Stack>


            {/******************************************
                    Curve Toggle, Set shape texture, Edit image
              ******************************************/}
            <Stack gap={1}>
              {
                (
                  (category === "image") ||
                  (category === "figma") ||
                  (category === "apple") ||
                  (category === "background") ||
                  (category === "microsoft") ||
                  (category === "floatgridsUi") ||
                  (category === "floatgridsLayout")
                ) && (
                  <CurveToggle curved={curved} curveAmount={curveAmount} id={id} />
                )
              }
            {category === "shape" && (
                <Group wrap="nowrap" gap={0}>
                  <Button onClick={() => onAddImageTextureClick()}
                    radius={4}
                    styles={editorModalPrimaryButtonStyles}
                    style={{
                      marginTop: 15,
                      borderTopRightRadius: texture ? 0 : 4, borderBottomRightRadius: texture ? 0 : 4
                    }}>
                    Texture</Button>
                  {texture && (<ActionIcon
                    onClick={() => onDeleteImageTextureClick()}
                    variant="filled"
                    color='black'
                    size={'xs'}
                    style={{ marginLeft: 2, height: 28, inline: true, marginTop: 15, fontSize: 12, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 32, borderBottomRightRadius: 32 }}
                  >
                    <IconTrash size={15} stroke={1.5} />
                </ActionIcon>)}
              </Group>)}

            {(category === "image") && (
                <Button onClick={() => onEditImageURLClick()} radius={4} styles={editorModalPrimaryButtonStyles} style={{ marginTop: 15 }}>
                    Edit image
                </Button>
            )}</Stack>

            {/******************************************
                    Refresh Figma, View creator, Copy, Lock, Delete
              ******************************************/}
            <Group justify="space-between" w={'100%'}>
              <Group gap={5}>
                {category === "figma" && isMine && <Tooltip label="Refresh frame">
                  <ActionIcon onClick={() => !isLocked && updateFrameURL()} size={'md'} variant="outline" aria-label="Settings" color={iconColour} style={{ borderColor: borderColour }}>
                    <IconRefresh style={{ width: '70%', height: '70%' }} stroke={1.5} />
                  </ActionIcon></Tooltip>}

                {category !== "ai" && category !== "shape" && category !== "text" && category !== "image" && category !== "figma" && category !== "apple" && category !== "microsoft" && category !== "UI" && category !== "floatgridsUi" && category !== "floatgridsLayout" && category !== "uploaded" &&
                  <Tooltip label="View creator">
                    <ActionIcon onClick={() => onAttrClick()} size={'md'} variant="outline" aria-label="Settings" color={iconColour} style={{ borderColor: borderColour }}>
                      <IconUserPin style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon></Tooltip>}
              </Group>
              <Group gap={5}>
                <Tooltip label="Edit object notes">
                  <ActionIcon onClick={() => setNotesOpen(true)} size={'md'} variant="outline" aria-label="Settings" color={iconColour} style={{ borderColor: borderColour }}>
                    <IconListDetails style={{ width: '70%', height: '70%' }} stroke={1.5} /></ActionIcon>
                </Tooltip>

                {category !== "figma" && <Tooltip label="Copy object">
                  <ActionIcon onClick={() => onCopyItemClick()} size={'md'} variant="outline" aria-label="Settings" color={iconColour} style={{ borderColor: borderColour }}>
                    <IconCopy style={{ width: '70%', height: '70%' }} stroke={1.5} /></ActionIcon>
                </Tooltip>}

                <Tooltip label="Lock controls">
                  <ActionIcon onClick={() => onLockItemClick()} size={'md'} variant="outline" aria-label="Settings" color={iconColour} style={{ borderColor: borderColour }}>
                    <IconLockOpen style={{ width: '70%', height: '70%' }} stroke={1.5} /></ActionIcon>
                </Tooltip>

                <Tooltip label="Delete object">
                  <ActionIcon onClick={() => removeObjectFunction({ currentObjectArtboard: currentObjectArtboard, id: id, undo: false, content: content, textKind: textKind, object: object, category: category, position: position, scale: scale, rotation: rotation, matrix: matrix, colour: colour, image: image, imageSize: imageSize, imageTexture: texture, curved: curved, curveAmount: curveAmount, pose: pose, uploadedURL: uploadedURL })} size={'md'} variant="subtle" aria-label="Settings" color={iconColour}>
                    <IconTrash stroke={1.5} /></ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Stack>
          
          
      </> 
      </Suspense>
    );
  }
};

export default LayerListItem;


const CurveToggle = ({curved, id, curveAmount}) => {

  const {updateCurved, updateCurveAmount, arrangeUndoRedo, currentObjectArtboard} = useObjectStore()
 
  const handleChange = () => {
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectUpdate", 
      artboard: currentObjectArtboard, 
      objectID: id,
      undo:false,
      field: "curved"
      })
      // send update to zustand
    updateCurved({id:id, undo: false})
  }
  const handleChangeCurveAmount = (e) => {
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectUpdate", 
      artboard: currentObjectArtboard, 
      objectID: id,
      undo:false,
      field: "curveAmount"
      })
      // send update to zustand
    updateCurveAmount({id:id, val:e, undo: false})
  }



  return (
    <div style={{width:"100%", paddingTop:8, paddingBottom:8}}>
    <Switch
      color="black"
      fw={500} size={'xs'}
      label="Curve"
      onChange={() => handleChange()} type="checkbox" name="curve" checked={curved}
    />
    {curved && <Slider
        color="black"
        defaultValue={curveAmount}
        onChange={(e) => handleChangeCurveAmount(e)}
        step={1}
        marks={[{ value: 0 },{ value: 3 },{ value: 6 },{ value: 9},{ value: 12 }]}
        min={1}
        max={12}
      />}
      </div>


  )
}


const AttributionModal = ({ attrOpen, onAttrCancel, id, category, object, pose, ModelAttributions }) => {
  const [attributionInfo, setAttributionInfo] = useState(null);

  useEffect(() => {
    if (attrOpen) {
      getSinglePose(pose.name).then((e) => setAttributionInfo(e.attribution));
    }
  }, [attrOpen, pose.name]);

  return (
    <Modal
      opened={attrOpen}
      onClose={onAttrCancel}
      title="Model Attribution"
      withCloseButton
      size="sm"
      radius={editorModalShell.radius}
      centered={editorModalShell.centered}
      overlayProps={editorModalShell.overlayProps}
      styles={editorModalStrongTitleStyles}
      zIndex={500}
    >
      <Stack gap="md">
        {attributionInfo ? (
          <Suspense key={id} fallback={null}>
            <Text size="sm" style={{ lineHeight: 1.55 }}>
              {attributionInfo}
            </Text>
          </Suspense>
        ) : (
          <Loader color="black" />
        )}
        <Group justify="flex-end">
          <Button onClick={() => onAttrCancel()} styles={editorModalPrimaryButtonStyles}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const NotesModal = ({notesOpen, onNotesCancel, id, objectNotes}) => {

  const {
    currentObjectArtboard,
    updateObjectNotes,
    arrangeUndoRedo
  } = useObjectStore();

  const [notes, setNotes] = useState(objectNotes);
  const [notesLoading, setNotesLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setNotesLoading(true);
    console.log(event)
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectUpdate", 
      artboard: currentObjectArtboard, 
      objectID: id,
      undo: false,
      field: "objectNotes"
    });

    try {
      // await the zustand function
      await updateObjectNotes(id, currentObjectArtboard, notes, false);
    } finally {
      // stop the loading spinner
     setNotesLoading(false);
    }
  }

  return (
    <Modal
    opened={notesOpen}
    onClose={onNotesCancel}
    centered
    padding={0}
    withCloseButton={false}
  ><>
    
    <Card style={{
      width: 400,
      borderRadius: 4,
      padding: 0,
      overflow: 'hidden'
    }}>
    <LoadingOverlay loaderProps={{ color: 'black' }} visible={notesLoading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />  
        <Text fw={700} size="md">Edit object notes</Text>
        <form onSubmit={handleSubmit}>
          <Textarea
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.currentTarget.value)}
            minRows={8}
            mb="md"
          />
          <Group justify="flex-end">
            <Button type="submit" radius={4} styles={editorModalPrimaryButtonStyles}>Submit</Button>
          </Group>
        </form>
 
    </Card>
    </>
  </Modal>
  )
}